import axios from "axios";
import { jwtDecode } from "jwt-decode";
import { store } from "../redux/store";
import { loginSuccess } from "../redux/slices/authSlice";

const baseURL = process.env.REACT_APP_BACKEND_API || "http://localhost:3001/";

/**
 * =========================
 * Base client
 * =========================
 */
const client = async (method, url, options = {}) => {
  if (!options) options = {};

  let urlencodedOptions = new URLSearchParams(Object.entries(options)).toString();

  const { data } = await axios({
    baseURL,
    url,
    method,
    data: urlencodedOptions,
    headers: {
      Accept: "*/*",
      "Content-Type": "application/x-www-form-urlencoded",
    },
    withCredentials: true,
  });

  if (method.toLowerCase() === "post") {
    if (data?.status === 200) return data?.data;
    else return data?.message;
  } else if (method.toLowerCase() === "get") {
    if (data?.status === 200) return data?.data;
    else if (data?.status === 500) return data?.message;
    else return data;
  } else if (method.toLowerCase() === "put") {
    if (data?.status === 200) return data?.data;
    else if (data?.status === 500) return data?.message;
    else return data;
  }
};

/**
 * =========================
 * Refresh token
 * - Thêm cơ chế khóa để tránh nhiều request cùng refresh một lúc
 * - Nếu refresh fail => clear store (logout)
 * =========================
 */
let refreshPromise = null;

const refreshToken = async () => {
  try {
    if (!refreshPromise) {
      refreshPromise = client("post", "/employee/refreshToken")
        .then((res) => {
          return res?.accessToken;
        })
        .finally(() => {
          refreshPromise = null;
        });
    }

    const newAT = await refreshPromise;

    // Nếu refresh API trả message/string hoặc không có accessToken => coi như fail
    if (!newAT || typeof newAT !== "string") {
      // clear auth state để FE về login
      store.dispatch(loginSuccess(null));
      return null;
    }

    return newAT;
  } catch (err) {
    console.log(err);
    store.dispatch(loginSuccess(null));
    return null;
  }
};

const axiosInstance = axios.create({
  baseURL,
  withCredentials: true,
});

/**
 * =========================
 * Request interceptor
 * - Nếu token hết hạn thì refresh trước khi gửi
 * =========================
 */
axiosInstance.interceptors.request.use(
  async (config) => {
    const state = store.getState();
    let user = state.auth.login?.currentUser;
    const hasAuthHeader =
      !!config?.headers?.Authorization || !!config?.headers?.authorization;
    if (hasAuthHeader) return config;

    if (user?.accessToken) {
      try {
        const currentTime = new Date().getTime() / 1000;
        const decodedToken = jwtDecode(user.accessToken);

        if (decodedToken?.exp && decodedToken.exp < currentTime) {
          const newAccessToken = await refreshToken();

          if (!newAccessToken) {
            // refresh fail
            return Promise.reject(new Error("Refresh token expired/invalid"));
          }

          user = { ...user, accessToken: newAccessToken };
          store.dispatch(loginSuccess(user));
        }

        config.headers = config.headers || {};
        config.headers["Authorization"] = `Bearer ${user.accessToken}`;
      } catch (error) {
        store.dispatch(loginSuccess(null));
        return Promise.reject(error);
      }
    }

    return config;
  },
  (error) => Promise.reject(error)
);

/**
 * =========================
 * Response interceptor
 * - Nếu BE trả 401/403 (token invalidated sớm, refresh bị xóa, user bị khóa...)
 *   => thử refresh 1 lần rồi retry request
 * - Không retry chính endpoint refreshToken để tránh loop
 * =========================
 */
axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const status = error?.response?.status;
    const originalRequest = error?.config;

    const isRefreshCall =
      originalRequest?.url?.includes("/employee/refreshToken");

    // Nếu 401/403 và chưa retry
    if (
      !isRefreshCall &&
      originalRequest &&
      (status === 401 || status === 403) &&
      !originalRequest._retry
    ) {
      originalRequest._retry = true;

      const newAccessToken = await refreshToken();

      if (!newAccessToken) {
        // refresh fail => logout
        store.dispatch(loginSuccess(null));
        return Promise.reject(error);
      }

      // cập nhật store + retry request
      const state = store.getState();
      const user = state.auth.login?.currentUser;

      store.dispatch(loginSuccess({ ...(user || {}), accessToken: newAccessToken }));

      originalRequest.headers = originalRequest.headers || {};
      originalRequest.headers["Authorization"] = `Bearer ${newAccessToken}`;

      return axiosInstance(originalRequest);
    }

    return Promise.reject(error);
  }
);

/**
 * =========================
 * clientAPI
 * =========================
 */
export const clientAPI = async (method, url, options = {}, accessToken = "") => {
  let reqData;

  const axiosConfig =
    accessToken && typeof accessToken === "object" ? accessToken : {};

  const token =
    typeof accessToken === "string" && accessToken ? accessToken : "";

  let headers = {
    Accept: "*/*",
    ...(axiosConfig.headers || {}),
  };

  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  if (options instanceof FormData) {
    reqData = options;
    headers["Content-Type"] = "multipart/form-data";
  } else if (typeof options === "object" && !(options instanceof URLSearchParams)) {
    reqData = JSON.stringify(options || {});
    headers["Content-Type"] = headers["Content-Type"] || "application/json";
  } else {
    reqData = new URLSearchParams(Object.entries(options || {})).toString();
    headers["Content-Type"] =
      headers["Content-Type"] || "application/x-www-form-urlencoded";
  }

  const response = await axiosInstance({
    url,
    method,
    data: reqData,
    ...axiosConfig,
    headers,
  });

  // Case riêng cho export/download file
  if (axiosConfig.responseType === "blob") {
    return response;
  }

  const { data } = response;

  if (method.toLowerCase() === "post") {
    if (data?.status === 200) return data?.data;
    else return data?.message;
  } else if (method.toLowerCase() === "get") {
    if (data?.status === 200) return data?.data;
    else if (data?.status === 500) return data?.message;
    else return data;
  } else if (method.toLowerCase() === "put") {
    if (data?.status === 200) return data?.data;
    else if (data?.status === 500) return data?.message;
    else return data;
  } else if (method.toLowerCase() === "delete") {
    if (data?.status === 200) return data?.data;
    else if (data?.status === 500) return data?.message;
    else return data;
  }
};

export { client, refreshToken, axiosInstance };
